import React, {useState} from 'react';
import {Button, Text, Icon, DropdownMenu} from '@gravity-ui/uikit';
import {TrashBin, Ellipsis, Grip, Copy, Pencil} from '@gravity-ui/icons';
import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {StoryScreenModel} from '../../../../entities';
import {useStoryScreensStore} from '../../model';
import {ActionModal} from '../../../../shared';
import {EditStoryScreenModal} from '../EditStoryScreenModal';
import styles from './StoryScreenCard.module.scss';

interface StoryScreenCardProps {
  screen: StoryScreenModel;
  storyId: string;
  isDragDisabled?: boolean;
  // Правки запрещены, пока идёт сохранение: до прихода обновлённых данных с сервера
  // у только что сохранённых медиа нет постоянного имени файла
  isEditDisabled?: boolean;
}

export const StoryScreenCard: React.FC<StoryScreenCardProps> = ({
  screen,
  storyId,
  isDragDisabled = false,
  isEditDisabled = false,
}) => {
  const {deleteScreen, duplicateScreen} = useStoryScreensStore();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
    id: screen.id!,
    disabled: isDragDisabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (screen.id) {
      deleteScreen(storyId, screen.id);
    }
    setIsDeleteModalOpen(false);
  };

  const handleDuplicate = () => {
    if (screen.id) {
      duplicateScreen(storyId, screen.id);
    }
  };

  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const getThemeClass = () => {
    return screen.theme === 'Dark' ? styles.darkTheme : styles.lightTheme;
  };

  // Определяем тип медиа (из локального поля или из серверных данных)
  const mediaType = screen.mediaType || screen.media?.type;
  const isVideo = mediaType === 'video';

  // URL изображения для фона (только для типа image): уменьшенная копия, если сервер её отдал
  const serverImage = screen.media?.type === 'image' ? screen.media.image : null;
  const backgroundImageUrl = !isVideo
    ? (screen.imagePreview || (serverImage ? serverImage.thumbnailUrl || serverImage.url : null) || null)
    : null;
  const backgroundStyle = backgroundImageUrl ? {backgroundImage: `url(${backgroundImageUrl})`} : {};

  // URL видео для превью (для типа video — показываем само видео)
  const videoUrl = isVideo
    ? (screen.videoPreview || (screen.media?.type === 'video' ? screen.media.video.url : null) || null)
    : null;

  const getTextVariant = (style: string) => {
    switch (style) {
      case 'h1':
        return 'header-1';
      case 'p2':
        return 'body-2';
      case 'c1':
        return 'caption-1';
      default:
        return 'body-1';
    }
  };

  const getButtonView = (theme: string) => {
    switch (theme) {
      case 'White':
        return 'outlined';
      case 'Black':
        return 'normal';
      case 'Default':
      default:
        return 'action';
    }
  };

  const getButtonClass = (theme: string) => {
    switch (theme) {
      case 'White':
        return `${styles.button} ${styles.whiteButton}`;
      case 'Black':
        return `${styles.button} ${styles.blackButton}`;
      default:
        return styles.button;
    }
  };

  const getContentClass = () => {
    return screen.alignToBottom ? `${styles.content} ${styles.alignedToBottom}` : styles.content;
  };

  const getDropdownItems = () => {
    return [
      {
        iconStart: <Icon size={16} data={Pencil} />,
        action: handleEdit,
        text: 'Редактировать',
        className: styles.dropdownItem,
      },
      {
        iconStart: <Icon size={16} data={Copy} />,
        action: handleDuplicate,
        text: 'Дублировать',
        className: styles.dropdownItem,
      },
      {
        iconStart: <Icon size={16} data={TrashBin} />,
        action: handleDelete,
        text: 'Удалить',
        className: styles.dropdownItemDelete,
      },
    ];
  };

  return (
    <div ref={setNodeRef} style={style} className={styles.screenCard} {...attributes}>
      <div className={styles.preview} style={backgroundStyle}>
        {/* Видео-бэкграунд для видео-экранов */}
        {isVideo && videoUrl && (
          <video src={videoUrl} className={styles.videoBackground} muted loop autoPlay playsInline />
        )}
        {/* Overlay для темы поверх изображения */}
        <div className={getThemeClass()}>
          {/* Иконка видео */}
          {isVideo && <div className={styles.videoIndicator}>▶</div>}
          {/* Drag handle в левом верхнем углу */}
          {!isDragDisabled && (
            <div className={styles.dragHandle} {...listeners}>
              <Icon data={Grip} size={16} />
            </div>
          )}

          {/* Меню действий в правом верхнем углу */}
          <div className={styles.topMenu}>
            <DropdownMenu
              size="s"
              icon={<Icon size={15} data={Ellipsis} />}
              items={getDropdownItems()}
              disabled={isEditDisabled}
            />
          </div>

          <div className={getContentClass()}>
            {/* Текстовый контент в flex-контейнере */}
            <div className={styles.textArea}>
              {screen.paragraphs
                .filter((paragraph) => paragraph.text.trim() !== '')
                .map((paragraph, index) => (
                  <Text
                    key={index}
                    variant={getTextVariant(paragraph.style) as any}
                    className={`${styles.paragraph} ${styles[`style_${paragraph.style}`] || ''}`}>
                    {paragraph.text}
                  </Text>
                ))}
            </div>

            {/* Кнопка всегда внизу */}
            {screen.button && screen.button.text && (
              <Button
                view={getButtonView(screen.button.theme) as any}
                size="l"
                width="max"
                className={getButtonClass(screen.button.theme)}>
                {screen.button.text}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Модалка подтверждения удаления */}
      <ActionModal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        setOpen={setIsDeleteModalOpen}
        caption="Удаление экрана"
        textButtonApply="Удалить"
        textButtonCancel="Отменить"
        size="s"
        buttonsSize="l"
        viewActionButton="outlined-danger"
        submitForm={confirmDelete}>
        <Text variant="body-2" color="secondary">
          Вы уверены, что хотите удалить этот экран?
        </Text>
      </ActionModal>

      {/* Модалка редактирования экрана */}
      <EditStoryScreenModal open={isEditModalOpen} setOpen={setIsEditModalOpen} storyId={storyId} screen={screen} />
    </div>
  );
};
